import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { PartLines } from '@/components/admin/protocoles/reading/analyst-reading-panel';
import { readingLines } from '@/lib/analyst-reading';
import {
  RANGEMENT_CONVERSATION_SENTENCE,
  RANGEMENT_NO_CONSIGNE_SENTENCE,
  RANGEMENT_NO_TARGET_SENTENCE,
  RANGEMENT_PART_BADGE,
  RANGEMENT_PART_LABEL,
  type RangementPartKind,
} from '@/lib/rangement-editor';
import { cn } from '@/lib/utils';
import type { ProtocolRangement, ProtocolStepDraft } from '@matter_extractor/shared';
import { rangerPromptInput, rangerSystemParts } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';
import { type ReactNode, useId, useState } from 'react';

/**
 * « Ce que le rangeur recevra » - le prompt du rangeur, en lecture seule, à la
 * frappe (scenario-succes.md §4 ; maquette `EditeurRangement`), sur le patron
 * de `analyst-reading-panel.tsx`.
 *
 * ## Rendered by the SERVER's functions
 *
 * The four parts of the system come from `rangerSystemParts` of `shared`,
 * assembled from `rangerPromptInput`, the pair the server serves the rangeur
 * with: the panel and « l'entrée servie » cannot part company, and no sentence
 * of the framing, the targets' layout or the contract is written in this
 * package. The input is the DRAFT's - the targets ticked, in the order of the
 * protocol, each with its direction, and the consigne with its
 * `{{objectif:id}}` resolved to the objective's current NAME, « (objectif
 * retiré) » for a ghost. The consigne is handed in TRIMMED, as the save stores
 * it.
 *
 * ## Five parts, four of the system and the conversation
 *
 * « Le cadre », « Les objectifs cibles », « La consigne », « La conversation »,
 * « Le contrat de sortie ». The conversation is NOT in the system block - it is
 * the one user message, which changes from session to session -, so it is drawn
 * as the maquette draws it, a sentence between the consigne and the contract.
 * The framing and the contract are GREYED and badged « en code »; the targets
 * say « des cases cochées »; the consigne, the one part this editor exists to
 * set, is framed in violet and never folds. Empty, its frame turns to a dashed
 * line and says so; the targets too.
 *
 * ## It shows the system and nothing else
 *
 * No fingerprint - a hash of a draft nobody has served names nothing - and no
 * conversation, which depends on a session.
 *
 * ## A region that scrolls, and that a keyboard can reach
 *
 * Its parts scroll inside the panel, and a zone a pointer can scroll and a
 * keyboard cannot would hide the contract: hence the tab stop on the region
 * (the arbitration of `analyst-reading-panel.tsx`).
 */

export interface RangementPanelProps {
  /** The section as the draft holds it - the panel trims the consigne itself. */
  readonly rangement: ProtocolRangement;
  /** The steps its targets and citations resolve on - the draft's, or the version « Voir » shows. */
  readonly steps: readonly ProtocolStepDraft[];
}

/** The two code parts that fold: the framing, read open, and the contract, shut. */
type FoldingKind = 'framing' | 'contract';

const OPENING: Readonly<Record<FoldingKind, boolean>> = { framing: true, contract: false };

export function RangementPanel({ rangement, steps }: RangementPanelProps) {
  const titleId = useId();
  const [open, setOpen] = useState<Readonly<Record<FoldingKind, boolean>>>(OPENING);
  const input = rangerPromptInput(steps, { ...rangement, consigne: rangement.consigne.trim() });
  const parts = rangerSystemParts(input);

  const system = (kind: RangementPartKind) => parts.find((part) => part.kind === kind)?.body ?? '';

  return (
    <section
      aria-labelledby={titleId}
      data-testid={PROTOCOL_TEST_IDS.rangementPanel}
      className="flex min-h-0 min-w-0 flex-col gap-3 rounded-md border border-gray-200 bg-gray-50 p-4 lg:max-h-[max(28rem,calc(100dvh-14rem))] lg:self-start"
    >
      <div className="flex shrink-0 flex-col gap-1">
        <h3 id={titleId} className="font-semibold text-bleu-nuit text-body">
          Ce que le rangeur recevra
        </h3>
        <p className="text-caption text-gray-600 leading-4">
          Le prompt du rangeur, assemblé à la frappe. Rien ne se modifie ici.
        </p>
      </div>
      <section
        aria-label="Prompt assemblé du rangeur"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the rule guards against tab stops that do nothing; this one scrolls.
        tabIndex={0}
        className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto pr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        <CodePart
          kind="framing"
          body={system('framing')}
          open={open.framing}
          onToggle={() => setOpen((was) => ({ ...was, framing: !was.framing }))}
        />
        <TargetsPart body={system('targets')} count={input.targets.length} />
        <ConsignePart body={system('consigne')} />
        <ConversationPart />
        <CodePart
          kind="contract"
          body={system('contract')}
          open={open.contract}
          onToggle={() => setOpen((was) => ({ ...was, contract: !was.contract }))}
        />
      </section>
    </section>
  );
}

/** « en code » / « des cases cochées » in grey, « ce protocole » in violet. */
function PartBadge({ kind }: { kind: RangementPartKind }) {
  const own = kind === 'consigne';
  const icon = own ? 'menu_book' : kind === 'targets' ? 'checklist' : 'lock';
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 font-semibold text-micro',
        own ? 'bg-violet-tint text-violet-deep' : 'bg-gray-100 text-gray-700',
      )}
    >
      <Icon name={icon} size={14} />
      {RANGEMENT_PART_BADGE[kind]}
    </span>
  );
}

interface PartFrameProps {
  readonly kind: RangementPartKind;
  readonly open?: boolean;
  readonly children: ReactNode;
}

/** The wrapper every part shares: its test mark, its kind, and whether it is open. */
function PartFrame({ kind, open = true, children }: PartFrameProps) {
  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.rangementPart}
      data-kind={kind}
      data-open={open}
      className="flex shrink-0 flex-col gap-1.5"
    >
      {children}
    </div>
  );
}

/** A part's head that does not fold: its label, and its badge. */
function PartHead({ kind, own = false }: { kind: RangementPartKind; own?: boolean }) {
  return (
    <div className="flex min-h-7 flex-wrap items-center justify-between gap-x-2 gap-y-1">
      <h4
        className={cn(
          'font-semibold text-caption uppercase tracking-wide',
          own ? 'text-violet-deep' : 'text-gray-600',
        )}
      >
        {RANGEMENT_PART_LABEL[kind]}
      </h4>
      <PartBadge kind={kind} />
    </div>
  );
}

interface CodePartProps {
  readonly kind: FoldingKind;
  readonly body: string;
  readonly open: boolean;
  readonly onToggle: () => void;
}

/** One part written in code: greyed, folded by its chevron, badged « en code ». */
function CodePart({ kind, body, open, onToggle }: CodePartProps) {
  const bodyId = useId();
  const chevron = open ? 'expand_more' : 'chevron_right';
  return (
    <PartFrame kind={kind} open={open}>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
          className="inline-flex min-h-7 cursor-pointer items-center gap-1.5 font-semibold text-caption text-gray-600 uppercase tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          <Icon name={chevron} size={14} />
          <span>{RANGEMENT_PART_LABEL[kind]}</span>
        </button>
        <PartBadge kind={kind} />
      </div>
      <div id={bodyId} hidden={!open} className="text-caption text-gray-600">
        <PartLines lines={readingLines(body)} />
      </div>
    </PartFrame>
  );
}

/**
 * The targets, as the system lists them: the number, the name, the identifier
 * and the direction - character for character, the layout being the code's.
 * Dashed, and saying there is none, while no target is ticked.
 */
function TargetsPart({ body, count }: { body: string; count: number }) {
  return (
    <PartFrame kind="targets">
      <PartHead kind="targets" />
      {count === 0 ? (
        <p
          data-testid={PROTOCOL_TEST_IDS.rangementTargetsServed}
          data-empty="true"
          className="rounded-md border border-gray-300 border-dashed px-3 py-2.5 text-caption text-gray-600"
        >
          {RANGEMENT_NO_TARGET_SENTENCE}
        </p>
      ) : (
        <p
          data-testid={PROTOCOL_TEST_IDS.rangementTargetsServed}
          data-empty="false"
          className="whitespace-pre-wrap break-words text-caption text-gray-600"
        >
          {body}
        </p>
      )}
    </PartFrame>
  );
}

/**
 * The protocol's part - the one that is THIS editor's to set: its heading in
 * violet, never folded, the consigne framed in violet and drawn AS SERVED,
 * blank lines and all (a panel that tidied what somebody typed would show a
 * consigne the rangeur never gets); dashed, and saying there is none, when the
 * consigne is empty.
 */
function ConsignePart({ body }: { body: string }) {
  const empty = body === '';
  return (
    <PartFrame kind="consigne">
      <PartHead kind="consigne" own />
      <div
        data-testid={PROTOCOL_TEST_IDS.rangementConsigneServed}
        data-empty={empty}
        className={cn(
          'rounded-md border-2 border-violet-deep bg-white px-3 py-2.5 text-caption',
          empty ? 'border-dashed font-semibold text-violet-deep' : 'text-gray-800',
        )}
      >
        {empty ? (
          <p>{RANGEMENT_NO_CONSIGNE_SENTENCE}</p>
        ) : (
          <p className="whitespace-pre-wrap break-words">{body}</p>
        )}
      </div>
    </PartFrame>
  );
}

/** The conversation: a sentence, since it is the user message and depends on a session. */
function ConversationPart() {
  return (
    <PartFrame kind="conversation">
      <PartHead kind="conversation" />
      <p className="text-caption text-gray-600">{RANGEMENT_CONVERSATION_SENTENCE}</p>
    </PartFrame>
  );
}
